<script>
export default {
  name: 'RentalBookingCard',
  props: {
    booking: {
      type: Object,
      required: true
    },
    openRescheduleModal: Function,
    openEditLabel: Function,
    openEditStatus: Function,
    openEditNotes: Function,
    openDeleteModal: Function,
    statusLabel: Function
  },
  computed: {
    statusColorClass() {
      const map = {
        upcoming: 'bg-blue-100 text-blue-700',
        requested: 'bg-yellow-100 text-yellow-700',
        confirmed: 'bg-green-100 text-green-700',
        rescheduled: 'bg-purple-100 text-purple-700',
        archive: 'bg-gray-200 text-gray-700',
        cancelled: 'bg-red-100 text-red-700',
        completed: 'bg-green-200 text-green-800',
        future: 'bg-cyan-100 text-cyan-700'
      };
      return map[this.booking.status] || 'bg-slate-100 text-slate-700';
    },
    accountId() {
      return this.$route.params.accountId;
    }
  },
  methods: {
    goToContact() {
      if (this.booking.customer_id) {
        this.$router.push({
          name: 'contact_profile_dashboard',
          params: {
            accountId: this.accountId,
            contactId: this.booking.customer_id.toString()
          }
        });
      }
    },
    goToConversation() {
      if (this.booking.conversation_id) {
        this.$router.push({
          name: 'inbox_conversation',
          params: {
            accountId: this.accountId,
            conversation_id: this.booking.conversation_id
          }
        });
      }
    }
  }
}
</script>

<template>
  <div
    class="relative border rounded-xl p-4 bg-white dark:bg-slate-800 shadow-md hover:shadow-lg transition-shadow duration-300 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
    :class="{ 'z-20': booking.showActions, 'z-10': !booking.showActions }"
  >
    <!-- Action Dropdown -->
    <div class="absolute top-2 right-2 z-40">
      <button
        @click="$set(booking, 'showActions', !booking.showActions)"
        class="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-700"
        aria-label="Toggle actions"
      >
        <fluent-icon icon="more-vertical" />
      </button>
      <div
        v-if="booking.showActions"
        class="z-50 absolute right-0 mt-2 w-36 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded shadow"
      >
        <button :disabled="!booking.customer_id" @click="goToContact" class="text-woot-500 w-full text-left px-4 py-2 hover:bg-slate-100 dark:hover:bg-slate-700">Go to contact</button>
        <button :disabled="!booking.conversation_id" @click="goToConversation" class="text-woot-500 w-full text-left px-4 py-2 hover:bg-slate-100 dark:hover:bg-slate-700">Message</button>
        <button @click="() => { openRescheduleModal(booking); $set(booking, 'showActions', false); }" class="w-full text-left px-4 py-2 hover:bg-slate-100 dark:hover:bg-slate-700">Reschedule</button>
        <button @click="() => { openEditStatus(booking); $set(booking, 'showActions', false); }" class="w-full text-left px-4 py-2 hover:bg-slate-100 dark:hover:bg-slate-700">Edit Status</button>
        <button @click="() => { openEditNotes(booking); $set(booking, 'showActions', false); }" class="w-full text-left px-4 py-2 hover:bg-slate-100 dark:hover:bg-slate-700">Edit Staff Notes</button>
        <button @click="() => { openDeleteModal(booking); $set(booking, 'showActions', false); }" class="w-full text-left px-4 py-2 hover:bg-slate-100 dark:hover:bg-slate-700 text-red-600">Cancel</button>
      </div>
      <div
        v-if="booking.showActions"
        class="fixed inset-0 z-40"
        @click="$set(booking, 'showActions', false)"
      ></div>
    </div>

    <!-- Booking Info -->
    <div class="space-y-2">
      <div class="text-lg font-semibold dark:text-white">
        Booking #{{ booking.booking_id }}
      </div>
      <div class="text-sm dark:text-white">
        Branch: <span class="font-medium">{{ booking.branch.branch_name || booking.branch_id }}</span>
      </div>
      <div class="text-sm dark:text-white">
        Customer: <span class="font-bold">{{ booking.contact.name || '-' }}</span>
      </div>
      <div class="flex items-center gap-2 text-sm dark:text-white">
        <fluent-icon icon="call" /> <span class="font-medium">{{ booking.contact.phone_number || '-' }}</span>
      </div>
      <div class="flex items-center gap-2 text-sm dark:text-white">
        <fluent-icon icon="mail" /> <span class="font-medium">{{ booking.contact.email || '-' }}</span>
      </div>
    </div>

    <!-- Product Info -->
    <div class="space-y-2">
      <!-- Product Image & Name -->
      <div class="flex items-center gap-4">
        <div class="w-20 h-20 flex-shrink-0 rounded border border-slate-200 dark:border-slate-700 overflow-hidden bg-slate-100 dark:bg-slate-700">
          <img
            v-if="booking.product.presigned_image_url"
            :src="booking.product.presigned_image_url"
            alt="Product"
            class="w-full h-full object-cover"
          />
          <div v-else class="w-full h-full flex items-center justify-center text-slate-400 text-xs dark:text-slate-500">
            No Image
          </div>
        </div>
        <div class="text-sm dark:text-white">
          <div class="text-slate-500 text-xs dark:text-slate-400">Product</div>
          <div class="font-semibold text-base">{{ booking.product.product_name || booking.product_id }}</div>
        </div>
      </div>

      <!-- Notes -->
      <div v-if="booking.customer_notes" class="text-sm dark:text-white">
        Customer Notes: <span class="font-medium">{{ booking.customer_notes }}</span>
      </div>
      <div v-if="booking.staff_notes" class="text-sm dark:text-white">
        Staff Notes: <span class="font-medium">{{ booking.staff_notes }}</span>
      </div>
    </div>

    <!-- Status + Payment + Date -->
    <div class="space-y-2 text-sm dark:text-white bg-slate-50 dark:bg-slate-900 p-3 rounded-lg">
      <div>
        Quantity: <span class="font-medium">{{ booking.quantity_rented }}</span>
      </div>
      <div>
        Total:
        <span class="font-medium">
          {{ new Intl.NumberFormat('en-US', { style: 'currency', currency: booking.currency || 'USD' }).format(booking.total_cost) }}
        </span>
      </div>
      <div class="flex items-center gap-2">
        Status:
        <span
          :class="['px-2 py-1 rounded-full text-xs font-semibold', statusColorClass]"
        >
          {{ statusLabel(booking.status) }}
        </span>
      </div>
      <div>
        <span class="font-semibold">Rental:</span>
        <template v-if="booking.before_reschedule_metadata">
          <div class="text-slate-500 dark:text-slate-400">
            <div>
              <span class="font-semibold">Before:</span> {{ booking.before_reschedule_metadata.rental_date }} • {{ booking.before_reschedule_metadata.rental_duration }} {{ booking.rental_unit }}<span v-if="booking.before_reschedule_metadata.rental_duration > 1">s</span>
            </div>
            <div>
              <span class="font-semibold">After:</span> {{ booking.rental_date }} • {{ booking.rental_duration }} {{ booking.rental_unit }}<span v-if="booking.rental_duration > 1">s</span>
            </div>
          </div>
        </template>
        <template v-else>
          {{ booking.rental_date }} • {{ booking.rental_duration }} {{ booking.rental_unit }}<span v-if="booking.rental_duration > 1">s</span>
        </template>
      </div>
    </div>
  </div>
</template>